import * as React from 'react';
import Link from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import {useEffect, useState} from "react";
import transactions from '../../models/transactions'
import helpers from "../../helpers/common";
import useRootHook from "../../hooks/useRoot";
import {Chip, Tooltip} from "@mui/material";
import {Link as RouterLink, useNavigate} from "react-router-dom";
import {Info, SettingsBackupRestore} from "@mui/icons-material";
import Pagination from "../Pagination";
import Typography from "@mui/material/Typography";
import TransactionDetailsDialog from "./TransactionDetailsDialog";
import IconButton from "@mui/material/IconButton";
import moment from "moment";
import TableToolbar from "../layout/TableToolbar";

const MAX_REFUND_DAYS = 30//todo confirm

export default function TransactionIndex({fullPage, searchParams, setSearchParams, refreshId, refresh, filters}) {
    fullPage = typeof fullPage === 'boolean' ? fullPage : true
    const filtersKey = (() => {
        try {
            if (!filters || typeof filters !== 'object') return ''
            return JSON.stringify(filters)
        } catch (e) {
            return ''
        }
    })()
    const [transactionsData, setTransactionsData] = useState(null)
    const [dialogIsOpened, setDialogIsOpened] = useState(false)
    const [selectedTransaction, setSelectedTransaction] = useState(null)
    const {setShowLoader, showConfirm, userData} = useRootHook()
    const navigate = useNavigate()
    const isMerchant = userData?.role === helpers.roles.M

    useEffect(() => {
        setShowLoader(true)
        let baseFilters = {}
        if (searchParams) {
            // Правильно обрабатываем множественные значения статусов
            baseFilters = Object.fromEntries(searchParams)
            // Если есть множественные статусы, преобразуем их в массив
            const statusParams = searchParams.getAll('status')
            if (statusParams.length > 0) {
                baseFilters.status = statusParams
            }
        } else if (filtersKey) {
            try {
                baseFilters = JSON.parse(filtersKey)
            } catch (e) {
                baseFilters = {}
            }
        }
        transactions.getData(baseFilters)
            .then((data) => {
                if (!data) {
                    setShowLoader(false)
                    return
                }
                data && setTransactionsData(data)
                setShowLoader(false)
            }).catch((err) => {
                setShowLoader(false)
        })
    }, [searchParams, refreshId, filtersKey, setShowLoader])

    const handleSetFilters = (field, value) => {
        if (setSearchParams) {
            if (searchParams.get(field) === value.toString()){
                searchParams.delete(field)
            } else {
                searchParams.set(field, value)
            }
            setSearchParams(searchParams)
        } else {
            navigate(`/transactions?${field}=${value}`)
        }
    }

    const handleRefund = (uuid) => {
        setShowLoader(true)
        transactions.refund(helpers.UUIDv4Transform(uuid))
            .then(() => helpers.sleep(3000))//Transaction will be updated by webhook, so delay the refresh
            .then(() => refresh)
            .catch((err) => {
                console.log(err)
                setShowLoader(false)
            })
    }

    if (!transactionsData) return null

    const now = moment()

    return (
        <React.Fragment>
            <TableToolbar
                title={fullPage ? 'Transactions' : 'Recent transactions'}
                actions={
                    !fullPage ? (
                        <Link color="primary" to="/transactions" component={RouterLink} underline="hover">
                            View all
                        </Link>
                    ) : null
                }
            />
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Customer</TableCell>
                        <TableCell>Country</TableCell>
                        <TableCell>Merchant</TableCell>
                        <TableCell>Project</TableCell>
                        {!isMerchant && <TableCell>Method</TableCell>}
                        <TableCell>Status</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {transactionsData.transactions.map((row) => {
                        // Определяем формат ключей для customer (может быть customer.* или customers.*)
                        const customerEmail = row['customer.email'] || row['customers.email'] || '';
                        const customerId = row['customer.id'] || row['customers.id'];
                        const customerCountry = row['customer.country'] || row['customers.country'];
                        
                        return <TableRow key={`tr-${row.id}`}>
                            <TableCell>
                                <Link onClick={() => handleSetFilters('customer_id', customerId)}
                                      underline="none" sx={{cursor: "pointer"}}>
                                    {helpers.cutLongString(customerEmail, 20)}
                                </Link>
                            </TableCell>
                            <TableCell>
                                {customerCountry ? customerCountry.toUpperCase() : '-'}
                            </TableCell>
                            <TableCell>
                                <Link onClick={() => handleSetFilters('merchant_id', row['merchant.id'])}
                                      underline="none" sx={{cursor: "pointer"}}>
                                    {helpers.cutLongString(row['merchant.name'], 20)}
                                </Link>
                            </TableCell>
                            <TableCell>
                                <Link onClick={() => handleSetFilters('project_id', row['project.id'])} underline="none"
                                      sx={{cursor: "pointer"}}>
                                    {helpers.cutLongString(row['project.name'], 20)}
                                </Link>
                            </TableCell>
                            {!isMerchant && (
                                <TableCell>
                                    <Link onClick={() => handleSetFilters('paymethod_id', row['paymethod.id'])}
                                          underline="none" sx={{cursor: "pointer"}}>
                                        {helpers.cutLongString(row['paymethod.name'], 20)}
                                    </Link>
                                </TableCell>
                            )}
                            <TableCell>
                                <Chip size="small" color={transactions.getChipStatusColor(row.status)} label={transactions.enumToStatus[row.status].capitalize()}/>
                            </TableCell>
                            <TableCell>
                                {helpers.getSubunitAmountString(row.amount_currency, row['currency.symbol'], row['currency.subunit'])}
                            </TableCell>
                            <TableCell>
                                <Typography variant="body2"
                                            sx={{fontWeight: 'bold'}}>{`${helpers.getTime(row.created)}`}</Typography>
                                <Typography variant="body2">{`${helpers.getDate(row.created)}`}</Typography>
                            </TableCell>
                            <TableCell>
                                <Tooltip title="Transaction details & callbacks">
                                    <IconButton onClick={() => {
                                        setSelectedTransaction(row)
                                        setDialogIsOpened(true)
                                    }}>
                                        <Info fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                {row.status === transactions.statusToEnum.completed
                                    && now.diff(moment(row.created), 'days') <= MAX_REFUND_DAYS
                                    && <Tooltip title="Refund transaction">
                                    <IconButton onClick={async () => {
                                        const confirmed = await showConfirm('Are you sure you want to refund this transaction?', 'Refund Transaction');
                                        if (confirmed) {
                                            handleRefund(row.uuid)
                                        }
                                    }}>
                                        <SettingsBackupRestore fontSize="small" color="error"/>
                                    </IconButton>
                                </Tooltip>}
                            </TableCell>
                        </TableRow>
                    })}
                </TableBody>
            </Table>
            {fullPage && <Pagination url={'/transactions'} count={transactionsData.count}/>}
            <TransactionDetailsDialog dialogIsOpened={dialogIsOpened} setDialogIsOpened={setDialogIsOpened} selectedTransaction={selectedTransaction} setSelectedTransaction={setSelectedTransaction} refresh={refresh}/>
        </React.Fragment>
    );
}